export const capitalize = (string: string): string => {
  if (!string) return ''
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const centsToDollars = (cents: number): number => cents / 100

export const dollarsToCents = (dollars: number): number => Math.round(dollars * 100)

export const dateToYearMonth = (date: string) => date && date.split('-').map(d => parseInt(d))

export const yearMonthToDate = (year: number, month: number): string => `${year}-${String(month).padStart(2, '0')}-01`

export const dateToYYYYMMDD = (date: Date): string => `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`

export const YYYYMMDDToDate = (date: string): Date => {
  if (!date) return new Date()
  const [year, month, day] = date.split('-')
  return new Date(Number(year), Number(month) - 1, Number(day))
}

export const dollarsToScientific = (dollars: number, precision: number = 2, prependDollarSign: boolean = true): string => {
  const postfixes = ['K', 'M', 'B', 'T']
  if (dollars < 1000) {
    return `${prependDollarSign ? '$' : ''}${dollars}`
  }
  if (dollars > 10 ** 15) {
    return 'Extraordinary big'
  }

  let index = 1
  while (dollars > (10 ** ((index + 1) * 3))) {
    index++
  }

  const actualValue = dollars / 10 ** ((index) * 3)
  const rounded = Math.round(actualValue * 10 ** precision) / 10 ** precision
  return `${prependDollarSign ? '$' : ''}${rounded}${postfixes[index - 1]}`
}

export const addToDate = (date: Date, years: number, months: number = 0, days: number = 0): Date =>
  new Date(date.getFullYear() + years, date.getMonth() + months, date.getDate() + days)

export const asDict = <T extends { id: string | number }, V>(items: T[], mapper: (x: T) => V): Record<string | number, V> => {
  return items.reduce<Record<string | number, V>>((acc, x) => { acc[x.id] = mapper(x); return acc }, {})
}

export const shuffleArray = <T>(array: T[]): T[] => array.map(value => ({ value, sort: Math.random() }))
  .sort((a, b) => a.sort - b.sort)
  .map(({ value }) => value)

export const floatToFixed = (number: number, decimals: number = 2): string => number.toFixed(decimals)

export const getYearRange = (startOffset: number, endOffset: number): number[] => {
  const currentYear = new Date().getFullYear()
  const years: number[] = []
  for (let i = currentYear + startOffset; i < currentYear + endOffset; i++) {
    years.push(i)
  }
  return years
}

export const centFieldsToDollars = (
  fieldNames: string[],
  item: Record<string, number>,
  { fromSuffix = '_cents', toSuffix = '_cents' }: { fromSuffix?: string; toSuffix?: string } = {},
) =>
  fieldNames.reduce((a, fieldName) => ({
    ...a,
    [fieldName + toSuffix]: centsToDollars(item[fieldName + fromSuffix])
  }), {} as Record<string, number>)

export const dollarFieldsToCents = (
  fieldNames: string[],
  item: Record<string, number>,
  { fromSuffix = '_cents', toSuffix = '_cents' }: { fromSuffix?: string; toSuffix?: string } = {},
) =>
  fieldNames.reduce((a, fieldName) => ({
    ...a,
    [fieldName + toSuffix]: dollarsToCents(item[fieldName + fromSuffix])
  }), {} as Record<string, number>)

interface PasswordValidationRule {
  regEx: RegExp
  message: string
}

export const passwordValidationRules: PasswordValidationRule[] = [
  {
    regEx: /(?=.*[A-Z])/,
    message: 'Must contain an uppercase letter',
  },
  {
    regEx: /(?=.*[a-z])/,
    message: 'Must contain a lowercase letter',
  },
  {
    regEx: /^\S*$/,
    message: 'Cannot contain spaces',
  },
  {
    regEx: /(?=.*[0-9])/,
    message: 'Must contain a number',
  },
  {
    regEx: /(?=.*[-!@#$%^&*])/,
    message: 'Must contain a special character',
  },
  {
    regEx: /^.{8,128}$/,
    message: 'Must be 8-128 characters long',
  },
]

// Shared display formatters. One place so units, thousands separators, money,
// and range collapsing are identical across every app (audit 2026-07-03 found
// "90MW" vs "170 MW", "30585" vs "58,691", "2022 - 2022" drift). UTC is pinned
// for dates so a user's timezone never shifts a seed-derived month.
export const formatNumber = (value: number, opts: { decimals?: number } = {}): string =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: opts.decimals ?? 0,
    maximumFractionDigits: opts.decimals ?? 0,
  }).format(value)

export const formatQuantity = (
  value: number,
  unit: string,
  opts: { decimals?: number } = {},
): string => `${formatNumber(value, opts)} ${unit}`

export const formatMoney = (
  dollars: number,
  opts: { decimals?: number; cents?: boolean } = {},
): string => {
  const amount = opts.cents ? dollars / 100 : dollars
  return `$${formatNumber(amount, { decimals: opts.decimals ?? 2 })}`
}

export const formatUnitRate = (
  dollars: number,
  unit: string,
  opts: { decimals?: number } = {},
): string => `${formatMoney(dollars, opts)}/${unit}`

export const formatMonthYear = (date: string | Date): string =>
  new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(typeof date === 'string' ? new Date(date) : date)

const isNil = (v: unknown): boolean => v === null || v === undefined || v === ''

export const formatRange = (
  from: number | string | null | undefined,
  to: number | string | null | undefined,
  opts: { dash?: string } = {},
): string => {
  const dash = opts.dash ?? ' - '
  if (isNil(from)) return isNil(to) ? '' : String(to)
  if (isNil(to) || String(from) === String(to)) return String(from)
  return `${from}${dash}${to}`
}

export const formatYearRange = (
  from: number | null | undefined,
  to: number | null | undefined,
): string => formatRange(from, to)

// Leaving the app (for IAM's login, or another app in the suite) goes through
// here so specs can mock it: jsdom does not let them replace window.location.
export const redirectTo = (url: string): void => window.location.replace(url)
